import * as React from 'react';

import { ApiError } from '@/domains/api';
import type { IListAggregates, IListCell, IListQuery, IListRow } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import type { ICriteriaProblem } from '../../list-criteria';
import { LIST_MAX_PAGE_SIZE } from '../../lists';
import { refusalMessage } from '../../refusal';
import { readPages } from './work-page/pager';

/**
 * A list's page and its totals, read together, and kept fresh by a poll (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3.5 and §3.7; survey-api's
 * §15.5, D11).
 *
 * **The page and the totals are one read** — `POST /lists/rows` and `POST /lists/aggregates` under
 * the same query, so the footer's Σ, the range's total and the group headings' counts answer the
 * rows drawn. A totals read that fails leaves the rows (the footer says the totals could not be
 * read); a rows read that fails fails the read. survey-api's 400 names every problem by its path,
 * handed up whole for the screen to place on its chips.
 *
 * **A result is drawn only under the query it answers** (`asked`): the last page stays on screen
 * while the next is out, and nothing does once this query's read has failed — the compliance
 * queues' rule. A later read that fails over this query's page says "Could not refresh — showing
 * the list as last read", never the first load's banner.
 *
 * **The poll** (D11): every 30 s on a visible tab, never over a read in flight, the page's
 * participants as `known` (at most 1,000 — a page holds no more) and **`since` the page read's
 * `asOf`, not the last poll's**. survey-api answers `entered` as a count of the rows that changed
 * since and are not known, over a window it opens two minutes early: a window moved on every poll
 * would count a row that joined once in two polls running, and a sum of them would grow on its own.
 * Held at the page read, each poll's `entered` and `left` are the whole change since the page was
 * read — said as they are, never added up — and the changed rows are patched in by participant and
 * survey (a row patched twice is patched with the same cells). `total` stays the page read's: the
 * range says what was read, and *Refresh* reads it afresh.
 *
 * **A row survey-api says left is struck, never removed** — "no longer matches this list" — and
 * **an entered row is said, never inserted** (§3.5): nothing moves under the pointer (§0.4).
 * *Refresh*, a page change and a new query read the page afresh, and the struck rows go.
 */

/** The poll's period, on a visible tab (D11). */
const POLL_MS = 30_000;

/** One page read, with what it answers. */
export interface IListRead {
  /** The query, page and totals asked — what the screen compares its own with. */
  asked: string;
  rows: IListRow[];
  total: number;
  /** survey-api's clock less its overlap — the poll's `since`, never drawn. */
  asOf: string;
  /** The wall clock when the read landed — the toolbar's "updated hh:mm" (§11.4). */
  readAt: Date;
  aggregates: Nullable<IListAggregates>;
  aggregatesError: Nullable<string>;
}

/** A failed read, as the screen says it: survey-api's problems to place, and a sentence. */
export interface IListReadError {
  asked: string;
  message: string;
  problems: readonly ICriteriaProblem[];
}

/** One cell a save's answer settles, patched into the page as read: `null` is a cell emptied. */
export interface IAdoptedCell {
  participantId: string;
  survey: string;
  field: string;
  cell: Nullable<IListCell>;
}

/** A row's key — a participant in a survey; on *All surveys* a person in two surveys is two rows. */
export function rowKeyOf(row: Pick<IListRow, 'participantId' | 'survey'>): string {
  return `${row.participantId}:${row.survey}`;
}

function failure(error: unknown, asked: string): IListReadError {
  const problems =
    error instanceof ApiError && error.status === 400 ? (error.problem?.problems ?? []) : [];
  return { asked, message: refusalMessage(error, 'Could not read this list'), problems };
}

export function useListRows({
  query,
  page,
  pageSize,
  pages = 1,
  sums,
  enabled = true,
  polled = true
}: {
  /** The read's query — the screen's, with every field its cells need. */
  query: IListQuery;
  page: number;
  pageSize: number;
  /** The pages read from `page` on, drawn as one — a work page's *Load more* (`work-page/pager.tsx`).
   *  The poll reads `page` alone, patching the rows it holds. */
  pages?: number;
  /** The summable columns shown — the footer's Σ and the headings' sums. */
  sums: readonly string[];
  enabled?: boolean;
  /** The 30 s poll — off for a report's preview, a question asked once (§4.2). */
  polled?: boolean;
}) {
  const asked = JSON.stringify({ query, page, pageSize, pages, sums });
  const [read, setRead] = React.useState<Nullable<IListRead>>(null);
  const [error, setError] = React.useState<Nullable<IListReadError>>(null);
  const [pollError, setPollError] = React.useState<Nullable<string>>(null);
  const [struck, setStruck] = React.useState<ReadonlySet<string>>(new Set());
  const [entered, setEntered] = React.useState(0);
  const [reading, setReading] = React.useState(false);

  const begun = React.useRef(0);
  /** The read out now, if any: a page read (`refresh`) or a poll. */
  const inFlight = React.useRef<Nullable<'refresh' | 'poll'>>(null);
  const controller = React.useRef<Nullable<AbortController>>(null);
  const readRef = React.useRef(read);
  readRef.current = read;
  const askedRef = React.useRef(asked);
  askedRef.current = asked;
  const body = React.useRef({ query, page, pageSize, pages, sums });
  body.current = { query, page, pageSize, pages, sums };

  /**
   * The page and its totals afresh — the struck rows and the entered count go with the old page.
   * Answers the rows read, or null where the read failed or a later one took its place.
   */
  const refresh = React.useCallback(async (): Promise<Nullable<IListRow[]>> => {
    controller.current?.abort();
    const mine = new AbortController();
    controller.current = mine;
    const seq = ++begun.current;
    const at = askedRef.current;
    const { query: q, page: p, pageSize: size, pages: count, sums: fields } = body.current;
    inFlight.current = 'refresh';
    setReading(true);
    try {
      const [rows, totals] = await Promise.all([
        readPages(
          (at, atSize) => listsApi.rows({ query: q, page: at, pageSize: atSize }, mine.signal),
          p,
          size,
          count,
          LIST_MAX_PAGE_SIZE
        ).then((parts) => ({ ...parts[0], rows: parts.flatMap((part) => part.rows) })),
        listsApi.aggregates({ query: q, fields: [...fields] }, mine.signal).then(
          (aggregates) => ({ aggregates, error: null }),
          (totalsError: unknown) => {
            if (mine.signal.aborted) throw totalsError;
            return {
              aggregates: null,
              error: refusalMessage(totalsError, 'The totals could not be read')
            };
          }
        )
      ]);
      if (seq !== begun.current) return null;
      setRead({
        asked: at,
        rows: rows.rows,
        total: rows.total,
        asOf: rows.asOf,
        readAt: new Date(),
        aggregates: totals.aggregates,
        aggregatesError: totals.error
      });
      setError(null);
      setPollError(null);
      setStruck(new Set());
      setEntered(0);
      return rows.rows;
    } catch (readError) {
      if (seq !== begun.current || mine.signal.aborted) return null;
      setError(failure(readError, at));
      return null;
    } finally {
      if (seq === begun.current) {
        inFlight.current = null;
        setReading(false);
      }
    }
  }, []);

  /**
   * One poll over the page on screen: changed rows patched, `left` struck, `entered` said. Skipped
   * before this query's page has landed and while a page read is out — that read is the fresher —
   * and, for the 30 s beat, while another poll is out. **`after` a save it is never skipped for a
   * poll out**: that one began before the save landed, so it is abandoned and this one asked (a
   * poll that lands after a save would patch the saved cells back). Answered `false` only when it
   * failed — a failure leaves the page as last read, said by the screen; a success takes back what
   * an earlier failure said, the stale banner included.
   */
  const poll = React.useCallback(async (after = false): Promise<boolean> => {
    const shown = readRef.current;
    if (shown === null || shown.asked !== askedRef.current) return true;
    if (inFlight.current === 'refresh' || (inFlight.current === 'poll' && !after)) return true;
    controller.current?.abort();
    const mine = new AbortController();
    controller.current = mine;
    const seq = ++begun.current;
    const { query: q, page: p, pageSize: size } = body.current;
    const known = [...new Set(shown.rows.map((row) => row.participantId))].slice(0, 1000);
    inFlight.current = 'poll';
    try {
      const answer = await listsApi.rows(
        { query: q, page: p, pageSize: size, since: shown.asOf, known },
        mine.signal
      );
      if (seq !== begun.current) return true;
      const changed = new Map(answer.rows.map((row) => [rowKeyOf(row), row]));
      setRead((current) =>
        current === null || current.asked !== shown.asked
          ? current
          : {
              ...current,
              rows: current.rows.map((row) => changed.get(rowKeyOf(row)) ?? row),
              readAt: new Date()
            }
      );
      setStruck(new Set(answer.left ?? []));
      setEntered(answer.entered ?? 0);
      setPollError(null);
      setError((now) => (now !== null && now.asked === shown.asked ? null : now));
      return true;
    } catch (pollFailure) {
      if (seq !== begun.current || mine.signal.aborted) return true;
      setPollError(refusalMessage(pollFailure, 'The list could not be read again'));
      return false;
    } finally {
      if (seq === begun.current) inFlight.current = null;
    }
  }, []);

  /**
   * What a save settled, patched into the page on screen — the value and the version a cell now
   * holds — so the grid draws it before the poll that follows the save lands. A row not on the page
   * is left alone; the poll's own patch, by row, is what the list ends on.
   *
   * **A read begun before the save landed is abandoned** — it would land after and draw the cells as
   * they were before the save. A page read is asked again, at once (its query is still the screen's);
   * a poll is not, since the save's own poll follows.
   */
  const adopt = React.useCallback(
    (cells: readonly IAdoptedCell[]) => {
      if (cells.length === 0) return;
      const out = inFlight.current;
      if (out !== null) {
        controller.current?.abort();
        begun.current += 1;
        inFlight.current = null;
        setReading(false);
      }
      const byRow = new Map<string, IAdoptedCell[]>();
      for (const cell of cells) {
        const key = rowKeyOf(cell);
        byRow.set(key, [...(byRow.get(key) ?? []), cell]);
      }
      setRead((current) =>
        current === null
          ? current
          : {
              ...current,
              rows: current.rows.map((row) => {
                const settled = byRow.get(rowKeyOf(row));
                if (!settled) return row;
                const next = { ...row.cells };
                for (const { field, cell } of settled) {
                  if (cell === null) delete next[field];
                  else next[field] = cell;
                }
                return { ...row, cells: next };
              })
            }
      );
      if (out === 'refresh') void refresh();
    },
    [refresh]
  );

  React.useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [asked, enabled, refresh]);

  React.useEffect(() => () => controller.current?.abort(), []);

  React.useEffect(() => {
    if (!enabled || !polled) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void poll();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, polled, poll]);

  // The last page while the next is out; nothing once this query's own read has failed.
  const failed = error?.asked === asked ? error : null;
  const shown = failed && read?.asked !== asked ? null : read;
  return {
    asked,
    read: shown,
    /** This query's failure — over no page, the read's error; over a page, a stale banner. */
    error: failed,
    pollError,
    struck,
    entered,
    reading,
    refresh,
    poll,
    adopt
  };
}
